import { createContext, useContext } from "react";
import type { AnswerValue, FileRef } from "../types";
import type { UploadRegistry } from "./uploadRegistry";

/**
 * What the file-upload control needs from whoever hosts the form.
 *
 * The control lives in this package and knows nothing about tRPC, response
 * ids, or auth headers. The app that renders a real fill implements this and
 * passes it through the fill session; the builder's preview passes nothing,
 * and the control draws a placeholder. Every method resolves to what the
 * SERVER now holds — file answers are server-written, so the control never
 * composes one itself.
 */
export interface FileUploadService {
  /**
   * Send one file for a question. Resolves to the file as attached — the
   * registry appends exactly that, never a server snapshot of the whole
   * list, so uploads and removals finishing in any order can't put a file
   * back or take one away that they didn't touch.
   */
  upload(params: {
    questionId: string;
    file: File;
    /** 0..1, as the bytes go out. */
    onProgress: (fraction: number) => void;
    signal: AbortSignal;
  }): Promise<FileRef>;
  /** Detach a file. Resolves to the updated answer, or undefined when the
   *  question is now unanswered. */
  remove(params: {
    questionId: string;
    fileId: string;
  }): Promise<AnswerValue | undefined>;
  /** A short-lived URL that downloads the file as an attachment. */
  downloadUrl(params: { fileId: string }): Promise<string>;
}

/**
 * A refusal the applicant can act on, in the server's own words — the
 * control shows `message` under the question. Anything else that rejects is
 * treated as "couldn't reach the server" and shown as such.
 */
export class FileUploadRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FileUploadRefusal";
  }
}

/**
 * What the host gives the control: the session's upload registry (see
 * `uploadRegistry.ts`), which owns every file change on the wire so that a
 * control unmounting — a hidden question, a section change — loses nothing.
 * Null in a preview, where the control draws a placeholder.
 */
export interface FileUploadsHost {
  registry: UploadRegistry | null;
}

const FileUploadsContext = createContext<FileUploadsHost>({ registry: null });

export const FileUploadsProvider = FileUploadsContext.Provider;

export function useFileUploads(): FileUploadsHost {
  return useContext(FileUploadsContext);
}
